import React, { memo, useCallback, useEffect, useMemo, useState } from "react";

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { useFilterGroup } from "@hooks/useFilterGroup";
import { useReduxSelector } from "@hooks/useReduxSelector";
import { SvgSortBy } from "@routes/IconeSvg";
import {
  birthAnniversaryActions,
  hiringAnniversaryActions
} from "@store/actions";

import "@css/pages/employer/listeEmployer.css";

import { Switch } from "@components/tailwindComponents";
import DotLoader from "@components/tailwindComponents/DotLoader";
import EmptyData from "@components/tailwindComponents/EmptyData";

import EmployeTabFilter from "../../components/EmployeTabFilter";
import ItemListAnniversary from "./ItemsAnniversary";

const Anniversary = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation("pages.employés.anniversary");
  const {
    list: birthList,
    // count: birthCount,
    isLoaded: hiringIsLoaded
  } = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.birthAnniversary
  );
  const {
    list: hiringList,
    // count: hiringCount,
    isLoaded: birthIsLoaded
  } = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.hiringAnniversary
  );

  const { isGroupIdsMatchFilter } = useFilterGroup();

  const [filteredBirthList, setFilteredBirthList] = useState([]);
  const [filteredHiringList, setFilteredHiringList] = useState([]);
  const [filterData, setFilterData] = useState({});
  const [isFiltering, setIsFiltering] = useState(false);
  // const [isSeeingMore, setIsSeeingMore] = useState(false);
  const [isHiring, setIsHiring] = useState(false);
  const [anniversaryList, setAnniversaryList] = useState([]);

  // const updateDimensions = () => {
  //   let elementResponsive = document.querySelectorAll(
  //     ".see_more_for_anniversary"
  //   )?.[1];
  //   if (
  //     elementResponsive?.clientHeight > 0 &&
  //     elementResponsive?.clientHeight >= elementResponsive?.scrollHeight &&
  //     !isSeeingMore
  //   ) {
  //     if (hiringList.length < hiringCount) {
  //       if (isHiring) setIsSeeingMore(true);
  //       dispatch(hiringAnniversaryActions.pushData);
  //     }
  //     if (birthList.length < birthCount) {
  //       if (!isHiring) setIsSeeingMore(true);
  //       dispatch(birthAnniversaryActions.pushData);
  //     }
  //   }
  // };

  // useEffect(() => {
  //   window.addEventListener("resize", updateDimensions);
  //   return () => {
  //     window.removeEventListener("resize", updateDimensions);
  //   };
  // }, []);

  useEffect(() => {
    const hiring = getFilteredList(hiringList, filterData);
    setFilteredHiringList(hiring);
    if (isFiltering) {
      setTimeout(() => {
        setIsFiltering(false);
      }, 0);
    }
  }, [hiringList, filterData]);

  useEffect(() => {
    const birth = getFilteredList(birthList, filterData);
    setFilteredBirthList(birth);
    if (isFiltering) {
      setTimeout(() => {
        setIsFiltering(false);
      }, 0);
    }
  }, [birthList, filterData]);

  useEffect(() => {
    if (isHiring) {
      setAnniversaryList(filteredHiringList);
    } else {
      setAnniversaryList(filteredBirthList);
    }
  }, [isHiring, filteredHiringList, filteredBirthList]);

  const birthAnniversary = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.birthAnniversary
  );
  const hiringAnniversary = useReduxSelector(
    ({ enterpriseEmployeData }) => enterpriseEmployeData.hiringAnniversary
  );

  useEffect(() => {
    if (!birthAnniversary.isLoaded) {
      dispatch(birthAnniversaryActions.loadData);
    } else if (birthAnniversary.isDataOutdated) {
      dispatch(birthAnniversaryActions.refreshData);
    }

    if (!hiringAnniversary?.isLoaded) {
      dispatch(hiringAnniversaryActions.loadData);
    } else if (hiringAnniversary.isDataOutdated) {
      dispatch(hiringAnniversaryActions.refreshData);
    }
  }, [
    birthAnniversary.isDataOutdated,
    birthAnniversary.isLoaded,
    dispatch,
    hiringAnniversary.isDataOutdated,
    hiringAnniversary.isLoaded
  ]);

  const isLoading = useMemo(
    () => !(isHiring ? hiringIsLoaded : birthIsLoaded),
    [isHiring, hiringIsLoaded, birthIsLoaded]
  );
  // const canSeeMore = useMemo(
  //   () =>
  //     !isLoading &&
  //     !isSeeingMore &&
  //     !isFiltering &&
  //     (isHiring
  //       ? hiringList.length < hiringCount
  //       : birthList.length < birthCount),
  //   [
  //     isSeeingMore,
  //     isFiltering,
  //     isHiring,
  //     isLoading,
  //     hiringList,
  //     hiringCount,
  //     birthList,
  //     birthCount
  //   ]
  // );

  // const handleSeeMoreClick = useCallback(() => {
  //   setIsSeeingMore(true);
  //   dispatch(
  //     isHiring
  //       ? hiringAnniversaryActions.pushData
  //       : birthAnniversaryActions.pushData
  //   );
  // }, [isHiring]);

  // const handleSeeMoreScroll = useCallback(
  //   (e) => {
  //     let td = e.target.querySelector("td");
  //     if (
  //       e.target.scrollHeight <=
  //       e.target.scrollTop + e.target.offsetHeight + td?.offsetHeight
  //     ) {
  //       handleSeeMoreClick();
  //     }
  //   },
  //   [handleSeeMoreClick]
  // );

  const handleHiringChange = useCallback(
    () => setIsHiring((_isHiring) => !_isHiring),
    []
  );

  const getFilteredList = (employesList, filter) => {
    const { employes, emplacements, groupes } = filter;
    return employesList.filter((employe) => {
      if (employes?.length && !employes.includes(employe._id)) {
        return false;
      }
      if (isNaN(employe.age)) {
        return false;
      }
      if (
        emplacements?.length &&
        !employe?.emplacement?.some((emplacement) =>
          emplacements.includes(emplacement.id)
        )
      ) {
        return false;
      }
      if (!isGroupIdsMatchFilter(employe, groupes)) {
        return false;
      }
      return true;
    });
  };

  const handleFiltering = useCallback(
    (filter) => {
      setIsFiltering(true);
      setFilterData(filter);

      // const arrayGroups = groupes
      //   .filter((activeArray) => activeArray.length === 1)
      //   .map((arrayGroup) => arrayGroup[0]);

      // const arraySubGroups = groupes
      //   .filter((activeArray) => activeArray.length > 1)
      //   .flatMap((arrayGroup) => {
      //     const output = [...arrayGroup];
      //     output.shift();
      //     return output;
      //   });

      // const filter = {
      //   Employer: employes,
      //   emplacements,
      //   groups: arrayGroups,
      //   subGroups: arraySubGroups
      // };

      // dispatch(birthAnniversaryActions.filterData(filter));
      // dispatch(hiringAnniversaryActions.filterData(filter));
    },
    [birthList, hiringList]
  );

  const handleSortAnniversaryDate = () => {
    if (anniversaryList.length > 1) {
      let sortedList = [...anniversaryList];
      sortedList.sort((a, b) => {
        const dateA = moment(a.anniversaryDate, "DD/MM/YYYY");
        const dateB = moment(b.anniversaryDate, "DD/MM/YYYY");
        return dateA.isBefore(dateB) ? 1 : -1;
      });
      if (
        sortedList[0].anniversaryDate === anniversaryList[0].anniversaryDate
      ) {
        sortedList.sort((a, b) => {
          const dateA = moment(a.anniversaryDate, "DD/MM/YYYY");
          const dateB = moment(b.anniversaryDate, "DD/MM/YYYY");
          return dateA.isBefore(dateB) ? -1 : 1;
        });
      }
      setAnniversaryList(sortedList);
    }
  };

  const handleSortAge = () => {
    if (anniversaryList.length > 1) {
      let sortedList = [...anniversaryList];
      sortedList.sort((a, b) => a.age - b.age);
      if (sortedList[0].age === anniversaryList[0].age) {
        sortedList.sort((a, b) => b.age - a.age);
      }
      setAnniversaryList(sortedList);
    }
  };

  return (
    <div className=" arh-space-y-4 arh-rounded arh-bg-white arh-p-4 arh-px-9">
      {/* Head Desktop */}
      <EmployeTabFilter onFilter={handleFiltering} />

      {/* Switch */}
      <div className="arh-w-full">
        <div className="arh-flex arh-w-full">
          <div className="arh-flex arh-items-center   arh-space-x-4 arh-py-6 arh-font-poppins arh-font-semibold arh-text-darkColor">
            <label className="!arh-mb-0 arh-text-[.813rem]">
              {t("Anniversaire de naissance")}
            </label>
            <Switch
              switchDouble={true}
              color="arh-bg-greenColor"
              handleChange={handleHiringChange}
              checked={isHiring}
            />
            <label className="!arh-mb-0 arh-text-[.813rem]">
              {t("Anniversaire de travail")}
            </label>
          </div>
        </div>
      </div>
      {/* End Switch */}

      {/* Content Desktop */}
      <div className="arh-w-full">
        <div
          className="see_more_for_anniversary arh-block arh-h-full arh-max-h-[calc(100dvh-82px)] arh-min-h-[50dvh] arh-w-full arh-overflow-auto"
          // onScroll={canSeeMore ? handleSeeMoreScroll : null}
        >
          <table className="arh-mt-0 arh-w-full arh-table-fixed arh-items-center">
            <thead className="arh-sticky arh-top-0 arh-z-10">
              <tr>
                <th className="arh-w-[20%] arh-bg-greyColor arh-px-4 arh-py-4  arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                  {t("Nom")}
                </th>
                <th className="arh-w-[20%] arh-bg-greyColor arh-px-4 arh-py-4 arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor">
                  {t("Prochain Anniversaire")}
                  <span
                    className="arh-ml-2 arh-cursor-pointer"
                    onClick={handleSortAnniversaryDate}
                  >
                    {SvgSortBy()}
                  </span>
                </th>
                <th
                  style={{ textAlign: "center" }}
                  className="arh-w-[16%] arh-bg-greyColor arh-px-4 arh-py-4 arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor"
                >
                  {t(isHiring ? "Employé depuis" : "Âge actuel")}
                  <span
                    className="arh-ml-2 arh-cursor-pointer"
                    onClick={handleSortAge}
                  >
                    {SvgSortBy()}
                  </span>
                </th>
                <th className="arh-bg-greyColor arh-px-4 arh-py-4 arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor ">
                  {t("Groupes")}
                </th>
                <th
                  style={{ textAlign: "start" }}
                  className="arh-w-[26%] arh-bg-greyColor arh-px-4 arh-py-4 arh-font-poppins arh-text-xs arh-font-semibold arh-text-darkColor"
                >
                  {t("Note")}
                </th>
                <th
                  style={{ textAlign: "center" }}
                  className=" arh-w-auto arh-bg-greyColor arh-px-4 arh-py-4"
                ></th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isFiltering ? (
                <tr>
                  <td colSpan={6}>
                    <div className="arh-h-[40dvh]">
                      <DotLoader size="small" />
                    </div>
                  </td>
                </tr>
              ) : !anniversaryList?.length ? (
                <tr>
                  <td colSpan={6}>
                    <EmptyData>{t("Aucun élément")}</EmptyData>
                  </td>
                </tr>
              ) : (
                anniversaryList.map((employe, index) => (
                  <ItemListAnniversary
                    key={employe._id}
                    employe={employe}
                    isHiringSelected={isHiring}
                  />
                ))
              )}
              {/* {isSeeingMore && (
                <tr key="loading">
                  <td colSpan="5">
                    <div className="arh-flex arh-justify-center arh-mb-6 arh-w-full">
                      <div className="arh-w-full arh-flex arh-flex-col arh-items-center justify-content-center">
                        <ThreeDotsLoader
                          class="inherit_from_parent"
                          color="#73C7B7"
                          height="100"
                          width="100"
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              )} */}
              {/* {canSeeMore && (
                <tr>
                  <td>
                    <button
                      className="view_less_more_app arh-font-poppins arh-font-semibold arh-text-greenColor"
                      onClick={handleSeeMoreClick}
                    >
                      {`... ${t("Voir Plus")}`}
                    </button>
                  </td>
                </tr>
              )} */}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default memo(Anniversary);
